import SectionTitle from "@Client/@Modules/application-standalone/components/helpers/section-title.vue";
import PageSectionGroupLayout from "@Client/@Modules/application-standalone/components/layout/page-section-group-layout.vue";
import { computed, defineComponent, toRefs, watch } from "@vue/composition-api";
import TextField from "@Shared/components/form-elements/fields/text-field.vue";
import SelectField from "@Shared/components/form-elements/fields/select-field.vue";
import RadioField from "@Shared/components/form-elements/fields/radio-field.vue";
import CurrencyField from "@Shared/components/form-elements/fields/currency-field.vue";
import AddressFieldGroup from "@Shared/components/form-elements/field-groups/address-field-group.vue";
import TimeAtFieldGroup from "@Shared/components/form-elements/field-groups/time-at-field-group.vue";
import { useApplicantHelpers } from "@Client/@Composables/application-standalone/pages/applicants/useApplicantHelpers";
import { APPLICATION_FIELD_OPTIONS } from "@Shared/@Utils/application-defaults-utils/global-field-options";
import AddressAutocompleteField from "@Shared/components/form-elements/fields/address-autocomplete-field.vue";
import { useAddressHelpers } from "@Shared/@Composables/application/useAddressHelpers";
import { useSummaryHelpers } from "@Client/@Composables/application-standalone/pages/summary/useSummaryHelpers";
import { APPLICATION_FLOW } from "@Shared/@Types/application/application-enums";
import { incomeHistoryValidator } from "@Client/@Composables/application-standalone/validations/incomeHistoryValidator";
import { incomeHistoryPageBindingsAndVisibilities } from "@Client/@Composables/application-standalone/pages/income-history/incomeHistoryPageBindingsAndVisibilities";
import NotificationPopover from "@Shared/components/general/notification-popover.vue";
import { get, reduce } from "lodash";
export default defineComponent({
  name: "IncomeHistoryInformation",
  components: {
    PageSectionGroupLayout,
    SectionTitle,
    TextField,
    SelectField,
    RadioField,
    CurrencyField,
    AddressFieldGroup,
    TimeAtFieldGroup,
    AddressAutocompleteField,
    NotificationPopover
  },
  props: {
    applicantIndex: {
      type: Number,
      required: true
    },
    incomeIndex: {
      type: Number,
      required: true
    },
    applicantId: {
      type: String,
      required: true
    },
    showErrors: {
      type: Number,
      default: 0
    }
  },
  setup(props, {
    emit
  }) {
    const {
      applicantIndex,
      incomeIndex,
      applicantId,
      showErrors
    } = toRefs(props);
    const {
      applicantNameFromIndex,
      allApplicants
    } = useApplicantHelpers();
    const applicantName = applicantNameFromIndex(applicantIndex.value);
    const {
      currentFlow
    } = useSummaryHelpers();
    const {
      yesNoOptions,
      applicantIncomeTypeOptions,
      applicantEmploymentTypeOptions,
      applicantOperatingAsOptions,
      applicantOccupationOptions,
      applicantIndustrySectorOptions,
      applicantIncomePeriodOptions
    } = APPLICATION_FIELD_OPTIONS();
    const {
      bindings,
      visibilities
    } = incomeHistoryPageBindingsAndVisibilities(applicantIndex, incomeIndex, applicantId);
    const {
      employerName,
      currentJob,
      companyType,
      employmentType,
      grossRevenue,
      incomeAmount,
      incomePeriod,
      incomeType,
      industrySector,
      jobTitle,
      monthsInIndustry,
      occupation,
      operatingAs,
      selfEmployed,
      timeAtJob,
      employmentWorkPhone,
      employmentAddress
    } = bindings;
    const {
      copyAddressObject
    } = useAddressHelpers();
    const handleEmployerAddressAutocomplete = employerAddress => {
      copyAddressObject(employmentAddress.value, employerAddress);
    };
    const {
      incomeEmployerNameVisible,
      incomeCurrentJobVisible,
      incomeCompanyTypeVisible,
      incomeEmploymentTypeVisible,
      incomeGrossRevenueVisible,
      incomeIncomeAmountVisible,
      incomeIncomePeriodVisible,
      incomeIncomeTypeVisible,
      incomeIndustrySectorVisible,
      incomeJobTitleVisible,
      incomeMonthsInIndustryVisible,
      incomeOccupationVisible,
      incomeOperatingAsVisible,
      incomeSelfEmployedVisible,
      incomeTimeAtJobVisible,
      incomeWorkPhoneVisible,
      incomeAddressVisible
    } = visibilities;
    const sectionVisible = computed(() => {
      return incomeCurrentJobVisible.value || incomeOccupationVisible.value || incomeEmployerNameVisible.value || incomeAddressVisible.value || incomeWorkPhoneVisible.value || incomeJobTitleVisible.value || incomeEmploymentTypeVisible.value || incomeIncomeAmountVisible.value || incomeIncomePeriodVisible.value || incomeSelfEmployedVisible.value || incomeOperatingAsVisible.value || incomeGrossRevenueVisible.value || incomeCompanyTypeVisible.value || incomeIncomeTypeVisible.value || incomeIndustrySectorVisible.value || incomeMonthsInIndustryVisible.value || incomeTimeAtJobVisible.value;
    });
    const {
      v$
    } = incomeHistoryValidator(applicantIndex, incomeIndex);
    const isValid = computed(() => {
      var _v$$value$$invalid, _v$$value;
      return (_v$$value$$invalid = !(v$ != null && (_v$$value = v$.value) != null && _v$$value.$invalid)) != null ? _v$$value$$invalid : false;
    });
    watch(showErrors, newVal => {
      if (newVal) {
        var _v$$value2;
        v$ == null ? void 0 : (_v$$value2 = v$.value) == null ? void 0 : _v$$value2.$touch();
      }
    });
    watch(isValid, newVal => {
      emit("disable-save", !newVal);
    }, {
      immediate: true
    });
    watch(selfEmployed, newVal => {
      if (newVal === false) {
        operatingAs.value = "";
      }
    });
    const hasTimeAtJobWarning = computed(() => {
      var _allApplicants$value$;
      const applicantIncomes = (_allApplicants$value$ = allApplicants.value[applicantIndex.value]) == null ? void 0 : _allApplicants$value$.employment;
      const totalApplicantTimeAtJob = reduce(applicantIncomes, (acc, income) => {
        const time = typeof income["time-at-job"] === "number" ? income["time-at-job"] : 0;
        acc = acc + time;
        return acc;
      }, 0);
      return totalApplicantTimeAtJob < 36;
    });
    const getValidator = path => get(v$.value, path, null);
    watch(jobTitle, newVal => {
      if (typeof newVal === "string") {
        const div = document.createElement("div");
        div.innerHTML = newVal;
        const sanitized = div.textContent || div.innerText || "";
        if (sanitized !== newVal) {
          jobTitle.value = sanitized;
        }
      }
    });
    return {
      applicantName,
      employerName,
      currentJob,
      companyType,
      employmentType,
      grossRevenue,
      incomeAmount,
      incomePeriod,
      incomeType,
      industrySector,
      jobTitle,
      monthsInIndustry,
      occupation,
      operatingAs,
      selfEmployed,
      timeAtJob,
      employmentWorkPhone,
      employmentAddress,
      yesNoOptions,
      applicantIncomeTypeOptions,
      applicantEmploymentTypeOptions,
      applicantOperatingAsOptions,
      applicantOccupationOptions,
      applicantIndustrySectorOptions,
      applicantIncomePeriodOptions,
      handleEmployerAddressAutocomplete,
      incomeEmployerNameVisible,
      incomeCurrentJobVisible,
      incomeCompanyTypeVisible,
      incomeEmploymentTypeVisible,
      incomeGrossRevenueVisible,
      incomeIncomeAmountVisible,
      incomeIncomePeriodVisible,
      incomeIncomeTypeVisible,
      incomeIndustrySectorVisible,
      incomeJobTitleVisible,
      incomeMonthsInIndustryVisible,
      incomeOccupationVisible,
      incomeOperatingAsVisible,
      incomeSelfEmployedVisible,
      incomeTimeAtJobVisible,
      incomeWorkPhoneVisible,
      incomeAddressVisible,
      sectionVisible,
      currentFlow,
      APPLICATION_FLOW,
      hasTimeAtJobWarning,
      v$,
      getValidator,
      allApplicants
    };
  }
});